import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { LineChart, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fmtCurrency, fmtPct } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function Market() {
  const { t } = useI18n();
  const [regions, setRegions] = useState([]);
  const [form, setForm] = useState({ region: "Lisboa", area: 80, monthly_rent: 1100, price: 280000 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/market/regions").then((res) => setRegions(res.data.regions)).catch(() => {});
  }, []);

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.post("/market/benchmark", {
        region: form.region,
        area: parseFloat(form.area) || 0,
        monthly_rent: parseFloat(form.monthly_rent) || 0,
        price: parseFloat(form.price) || 0,
      });
      setResult(res.data);
    } catch (e) {
      toast.error("Error");
    } finally {
      setLoading(false);
    }
  };

  const verdictMeta = {
    underpriced: { icon: TrendingUp, color: "hsl(var(--success))", label: t("verdict_underpriced") },
    overpriced: { icon: TrendingDown, color: "hsl(var(--destructive))", label: t("verdict_overpriced") },
    on_market: { icon: Minus, color: "hsl(var(--warning))", label: t("verdict_on_market") },
  };

  return (
    <div className="space-y-6" data-testid="market-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-sm bg-secondary flex items-center justify-center">
          <LineChart className="h-5 w-5 text-[hsl(var(--success))]" />
        </div>
        <div>
          <h1 className="font-display text-4xl">{t("market_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("market_sub")}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 rounded-sm border-border space-y-4">
          <div className="space-y-2">
            <Label>{t("region")}</Label>
            <Select value={form.region} onValueChange={(v) => setForm({ ...form, region: v })}>
              <SelectTrigger data-testid="market-region" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover">
                {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>{t("area")}</Label>
            <Input type="number" data-testid="market-area" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} className="rounded-sm font-mono" /></div>
          <div className="space-y-2"><Label>{t("your_rent")}</Label>
            <Input type="number" data-testid="market-rent" value={form.monthly_rent} onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })} className="rounded-sm font-mono" /></div>
          <div className="space-y-2"><Label>{t("your_price")}</Label>
            <Input type="number" data-testid="market-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-sm font-mono" /></div>
          <Button onClick={run} disabled={loading} data-testid="market-run" className="w-full rounded-sm">{loading ? "…" : t("run_benchmark")}</Button>
        </Card>

        <div>
          {!result ? (
            <Card className="p-16 rounded-sm border-dashed border-border h-full flex flex-col items-center justify-center text-center">
              <LineChart className="h-10 w-10 text-muted-foreground mb-3" />
              <p className="text-muted-foreground">{t("run_benchmark")}</p>
            </Card>
          ) : (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="market-result">
              {(() => {
                const v = verdictMeta[result.verdict] || verdictMeta.on_market;
                const V = v.icon;
                return (
                  <Card className="p-6 rounded-sm border-border" style={{ borderColor: v.color }}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <V className="h-6 w-6" style={{ color: v.color }} />
                        <span className="font-display text-xl">{v.label}</span>
                      </div>
                      <span className="text-xs text-muted-foreground" data-testid="market-data-source">
                        {result.data_source === "live" ? t("data_source_live") : t("data_source_static")}
                      </span>
                    </div>
                  </Card>
                );
              })()}
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-5 rounded-sm border-border">
                  <span className="text-xs uppercase text-muted-foreground">{t("market_rent_est")}</span>
                  <p className="font-display text-2xl tabular-nums mt-1">{fmtCurrency(result.your.market_rent_estimate)}</p>
                  {result.your.rent_delta_pct !== null && (
                    <p className={`text-xs mt-1 ${result.your.rent_delta_pct < 0 ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]"}`}>
                      {t("rent_vs_market")}: {result.your.rent_delta_pct > 0 ? "+" : ""}{result.your.rent_delta_pct}%
                    </p>
                  )}
                </Card>
                <Card className="p-5 rounded-sm border-border">
                  <span className="text-xs uppercase text-muted-foreground">{t("market_price_est")}</span>
                  <p className="font-display text-2xl tabular-nums mt-1">{fmtCurrency(result.your.market_price_estimate)}</p>
                </Card>
                <Card className="p-5 rounded-sm border-border">
                  <span className="text-xs uppercase text-muted-foreground">{t("your_yield")}</span>
                  <p className="font-display text-2xl tabular-nums mt-1">{result.your.your_gross_yield !== null ? fmtPct(result.your.your_gross_yield) : "—"}</p>
                </Card>
                <Card className="p-5 rounded-sm border-border">
                  <span className="text-xs uppercase text-muted-foreground">{t("market_yield")}</span>
                  <p className="font-display text-2xl tabular-nums mt-1">{fmtPct(result.reference.market_gross_yield)}</p>
                </Card>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
